'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'
import { ArrowUp, Mail, MessageCircle, Phone } from 'lucide-react'

export const WHATSAPP_URL = 'https://wa.me/923001301223'
export const PHONE_URL = 'tel:+923001301223'

const TEXT = {
  en: { whatsapp: 'WhatsApp', call: 'Call', contact: 'Contact', top: 'Back to top', dock: 'Quick actions' },
  ja: { whatsapp: 'WhatsApp', call: '電話', contact: 'お問い合わせ', top: 'トップへ', dock: 'クイックアクション' },
}

/**
 * Always-visible quick actions.
 *  - Desktops (lg+): WhatsApp and back-to-top on the right edge
 *    (Apply, phone and email are in the header).
 *  - Phones and tablets: a bottom bar with WhatsApp, Call and Contact; Apply stays in the header
 *    (the page gets matching bottom padding in layout.tsx).
 */
export function QuickDock() {
  const pathname = usePathname() ?? ''
  const off = /\/(admin|portal)(\/|$)/.test(pathname)
  const locale: 'en' | 'ja' = pathname.startsWith('/ja') ? 'ja' : 'en'
  const t = TEXT[locale]
  const prefix = `/${locale}`
  const [scrolled, setScrolled] = useState(false)

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 600)
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [])

  if (off) return null
  const onContactPage = pathname.endsWith('/contact')

  const iconBtn =
    'group relative flex h-11 w-11 items-center justify-center rounded-full bg-white/95 text-brand-green shadow-[0_10px_30px_-12px_rgba(20,33,61,0.45)] ring-1 ring-brand-cool-gray backdrop-blur transition-all duration-300 hover:-translate-x-0.5 hover:bg-brand-green hover:text-white hover:ring-brand-green focus-ring'
  const tip =
    'pointer-events-none absolute right-full mr-3 whitespace-nowrap rounded-full bg-brand-navy px-3 py-1.5 text-xs font-medium text-white opacity-0 shadow-lg transition-all duration-200 translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-focus-visible:opacity-100'

  return (
    <>
      {/* Desktop */}
      <nav aria-label={t.dock} className="fixed bottom-6 right-4 z-[45] hidden flex-col items-end gap-2.5 lg:flex xl:right-6">
        <button
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label={t.top}
          className={`${iconBtn} ${scrolled ? 'opacity-100' : 'pointer-events-none translate-y-2 opacity-0'}`}
        >
          <ArrowUp className="h-5 w-5" />
          <span className={tip}>{t.top}</span>
        </button>
        <a
          href={WHATSAPP_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t.whatsapp}
          className={`${iconBtn} !bg-[#25D366] !text-white !ring-[#25D366] hover:!bg-[#1ebe5b]`}
        >
          <MessageCircle className="h-5 w-5" />
          <span className="absolute inset-0 -z-10 animate-ping rounded-full bg-[#25D366]/40 [animation-duration:2.4s]" />
          <span className={tip}>{t.whatsapp}</span>
        </a>
      </nav>

      {/* Phones and tablets */}
      <nav
        aria-label={t.dock}
        className="fixed inset-x-0 bottom-0 z-[45] border-t border-brand-cool-gray/70 bg-white/95 pb-[env(safe-area-inset-bottom)] shadow-[0_-10px_30px_-18px_rgba(20,33,61,0.5)] backdrop-blur-xl lg:hidden"
      >
        <div className="mx-auto grid h-16 max-w-md grid-cols-3 items-center px-2 text-[11px] font-medium text-brand-charcoal">
          <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="flex flex-col items-center gap-1 py-1">
            <MessageCircle className="h-5 w-5 text-[#1ebe5b]" />
            {t.whatsapp}
          </a>
          <a href={PHONE_URL} className="flex flex-col items-center gap-1 py-1">
            <Phone className="h-5 w-5 text-brand-green" />
            {t.call}
          </a>
          <Link
            href={`${prefix}/contact`}
            aria-current={onContactPage ? 'page' : undefined}
            onClick={() => onContactPage && window.scrollTo({ top: 0, behavior: 'smooth' })}
            className={`flex flex-col items-center gap-1 py-1 ${onContactPage ? 'font-semibold text-brand-red' : ''}`}
          >
            <Mail className={`h-5 w-5 ${onContactPage ? 'text-brand-red' : 'text-brand-navy'}`} />
            {t.contact}
          </Link>
        </div>
      </nav>
    </>
  )
}
